import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toast } from '../lib/toast.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

/**
 * The Roles page.
 *
 * Four of the six features are lists a server adds rows to, which is the first
 * page here to need that. The rule it follows: **a row is read relative to
 * itself**, by `data-field`, never by a form-wide name.
 *
 * Indexed names (`auto.0.roleId`) are the obvious alternative and they break
 * silently. Delete the middle row of three and the names no longer match the
 * positions; the page still saves, and it saves the wrong rule against the
 * wrong role. Reading each row on its own has no index to get wrong.
 */
export function initRolesPage(): void {
  const found = $<HTMLFormElement>('[data-roles-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  wireLists(form);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/roles?guildId=${guildId}`, {
        guildId,

        autoRoles: rows(form, 'auto')
          .map((row) => ({
            roleId: select(row, 'roleId'),
            action: choice(row, 'action', 'add'),
            target: choice(row, 'target', 'all'),
            delaySeconds: Math.round(duration(text(row, 'delay')) / 1000),
            removeOnNewRole: checked(row, 'removeOnNewRole'),
          }))
          // A rule with no role is a row someone started and left: saving it
          // would fail validation and lose the rest of the page with it.
          .filter((rule) => rule.roleId !== ''),

        roleme: {
          enabled: toggle(form, 'roleme.enabled'),
          roleIds: findSelectMenu(form, 'roleme.roleIds')?.value ?? [],
        },

        temporaryRoles: rows(form, 'temporary')
          .map((row) => ({
            roleId: select(row, 'roleId'),
            mode: choice(row, 'mode', 'expire'),
            durationSeconds: Math.round(duration(text(row, 'duration')) / 1000),
            onReassign: choice(row, 'onReassign', 'reset'),
          }))
          // The duration is what makes it temporary; without one there is
          // nothing to schedule.
          .filter((rule) => rule.roleId !== '' && rule.durationSeconds > 0),

        persistentRoles: rows(form, 'persistent')
          .map((row) => ({
            roleId: select(row, 'roleId'),
            onManualRemoval: choice(row, 'onManualRemoval', 'drop'),
          }))
          .filter((rule) => rule.roleId !== ''),

        voiceRoles: rows(form, 'voice')
          .map((row) => ({
            roleId: select(row, 'roleId'),
            // An empty channel is the meaningful default - "any voice channel" -
            // so only the role decides whether the row is worth keeping.
            channelId: nativeSelect(row, 'channelId'),
          }))
          .filter((rule) => rule.roleId !== ''),

        seniorityRoles: rows(form, 'seniority')
          .map((row) => ({
            roleId: select(row, 'roleId'),
            afterSeconds: Math.round(duration(text(row, 'after')) / 1000),
            target: choice(row, 'target', 'all'),
          }))
          // Without a threshold there is nothing to reach: the row is one
          // somebody started and left, and sending it would fail validation and
          // lose the rest of the page with it.
          .filter((rule) => rule.roleId !== '' && rule.afterSeconds > 0),

        tagRole: {
          enabled: toggle(form, 'tagRole.enabled'),
          roleId: findSelectMenu(form, 'tagRole.roleId')?.single ?? '',
        },

        factions: {
          enabled: toggle(form, 'factions.enabled'),
          list: rows(form, 'faction')
            .map((row) => ({
              key: row.dataset.key ?? '',
              name: text(row, 'name'),
              description: text(row, 'description'),
              roleIds: findSelectMenu(row, 'roleIds')?.value ?? [],
              cooldownSeconds: Math.round(duration(text(row, 'cooldown')) / 1000),
            }))
            // The name is the faction: it is what a member types into
            // `/faction join`, and an unnamed one could never be joined.
            .filter((faction) => faction.name !== ''),
        },
      });
    },
  });

  /*
   * Wired last, deliberately.
   *
   * `trackDirtyForm` is armed above before anything else touches the page: if
   * this listener threw while binding, saving would still work. A settings page
   * that cannot be saved because a button beside it failed is the failure this
   * ordering exists to prevent (AGENTS §3.5).
   */
  wireBackfill(form, guildId);
}

/* ------------------------------------------------------------------ the scan */

/**
 * The retroactive scan.
 *
 * Not part of the form: it is an action, not a setting, and sending it with the
 * save would make every correction of a typo download the whole member list.
 */
function wireBackfill(form: HTMLFormElement, guildId: string): void {
  const button = $<HTMLButtonElement>('[data-backfill]', form);
  const status = $<HTMLElement>('[data-backfill-status]', form);
  if (!button) return;

  button.addEventListener('click', () => {
    void (async () => {
      // The scan walks every member: on a large server it is seconds, not
      // milliseconds, and a button that looks idle invites a second click.
      button.disabled = true;
      const original = button.textContent;
      button.textContent = ct('backfillRunning', 'Scanning…');

      try {
        const answer = await api.post<{ report: BackfillReport }>(
          `/api/roles/backfill?guildId=${guildId}`,
          { guildId },
        );

        if (status) {
          /*
           * Substituted by `ct` rather than by hand.
           *
           * A concatenated sentence has no key, and that is precisely how
           * `Retirer ${role.name}` stayed French in all sixteen languages.
           */
          status.textContent = ct(
            'backfillDone',
            '{seen} member(s) examined, {granted} role(s) granted, {scheduled} scheduled.',
            {
              seen: String(answer.report.seen),
              granted: String(answer.report.granted),
              scheduled: String(answer.report.scheduled),
            },
          );
        }
      } catch (error) {
        // Surfaced, never swallowed. A refused scan - too soon, bot absent -
        // must say so: a button that appears to do nothing is worse than one
        // that explains why it did nothing.
        toast(error instanceof Error ? error.message : String(error), 'error');
      } finally {
        // Restored whatever happened: `api` surfaces the error itself, and a
        // button left disabled after a refused scan would look like a bug on
        // top of the refusal.
        button.disabled = false;
        button.textContent = original;
      }
    })();
  });
}

interface BackfillReport {
  seen: number;
  skipped: number;
  granted: number;
  removed: number;
  scheduled: number;
}

/* ------------------------------------------------------------------ the rows */

/** Wires the add and remove buttons of every list on the page. */
function wireLists(form: HTMLFormElement): void {
  for (const button of $$<HTMLButtonElement>('[data-add]', form)) {
    const kind = button.dataset.add ?? '';
    button.addEventListener('click', () => {
      addRow(form, kind);
    });
  }

  /*
   * Removal is delegated from the list rather than bound per row: rows added
   * after load would otherwise need their own binding, and forgetting it gives
   * a delete button that silently does nothing.
   */
  for (const list of $$<HTMLElement>('[data-list]', form)) {
    list.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest('[data-remove]');
      if (!button) return;

      button.closest('[data-rule-row]')?.remove();
      refreshEmptyStates(form);
      form.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  refreshEmptyStates(form);
}

/** Counter behind the unique radio-group names of rows added in the browser. */
let added = 0;

function addRow(form: HTMLFormElement, kind: string): void {
  const template = $<HTMLTemplateElement>(`[data-template="${CSS.escape(kind)}"]`);
  const list = $<HTMLElement>(`[data-list="${CSS.escape(kind)}"]`, form);
  if (!template || !list) return;

  added += 1;

  /*
   * The blueprint's radio names carry a placeholder, replaced here with a
   * counter. Two rows sharing a radio name share one group, and picking
   * "Remove" on the new row would clear the choice on an existing one.
   *
   * The counter only ever goes up, so a row added, deleted and added again
   * never reuses a name a stale node might still answer to.
   */
  const markup = template.innerHTML.replaceAll('__ROW__', `new${String(added)}`);

  const holder = document.createElement('div');
  holder.innerHTML = markup;

  const row = holder.querySelector('[data-rule-row]');
  if (!row) return;

  // A faction needs a key before it is saved: it is what ties a member's stored
  // choice to the faction, and it has to survive the faction being renamed.
  if (kind === 'faction' && row instanceof HTMLElement) {
    row.dataset.key = `f${String(Date.now())}${String(added)}`;
  }

  list.append(row);

  // The blueprint's `<select>` tags are still native: they are inert markup
  // inside a `<template>` and were never upgraded with the rest of the page.
  upgradeSelects(document);

  refreshEmptyStates(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));

  row.querySelector<HTMLElement>('input, [data-select-host]')?.focus();
}

/** Shows the "nothing here yet" note only when a list is genuinely empty. */
function refreshEmptyStates(form: HTMLFormElement): void {
  for (const note of $$<HTMLElement>('[data-empty]', form)) {
    const kind = note.dataset.empty ?? '';
    const list = $<HTMLElement>(`[data-list="${CSS.escape(kind)}"]`, form);
    note.hidden = (list?.querySelectorAll('[data-rule-row]').length ?? 0) > 0;
  }
}

function rows(form: HTMLFormElement, kind: string): HTMLElement[] {
  return $$<HTMLElement>(`[data-list="${CSS.escape(kind)}"] [data-rule-row]`, form);
}

/* ---------------------------------------------------------------- the fields */

function field(row: ParentNode, name: string): Element | null {
  return row.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

function text(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function checked(row: ParentNode, name: string): boolean {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

/** The ticked option of a card group, by its wrapper rather than its name. */
function choice(row: ParentNode, name: string, fallback: string): string {
  const wrapper = field(row, name);
  const ticked = wrapper?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return ticked?.value || fallback;
}

/** The single value of an upgraded menu inside a row. */
function select(row: ParentNode, name: string): string {
  return findSelectMenu(row, name)?.single ?? '';
}

/**
 * The value of a plain `<select>` inside a row.
 *
 * The voice-channel picker stays native rather than becoming a `data-select-menu`
 * because it needs a real empty option meaning "any channel", and the upgraded
 * component reads an empty value as "nothing chosen yet".
 */
function nativeSelect(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLSelectElement ? node.value : '';
}

/** A page-level toggle, read by its form name. */
function toggle(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

/**
 * Reads `7d`, `2h30m`, `45s` into milliseconds; a bare number is minutes.
 *
 * Same grammar as the moderation durations, deliberately: a server that has
 * learned to type `7d` in one field should not discover it means something else
 * in another.
 */
function duration(input: string): number {
  const units: Record<string, number> = {
    s: 1000,
    m: 60_000,
    h: 3_600_000,
    d: 86_400_000,
    w: 604_800_000,
  };
  let total = 0;

  for (const match of input.toLowerCase().matchAll(/(\d+)\s*([smhdw])/g)) {
    total += Number(match[1]) * (units[match[2] ?? ''] ?? 0);
  }

  if (total === 0 && /^\d+$/.test(input)) total = Number(input) * 60_000;
  return total;
}
